import { Link } from '@inertiajs/react';
import React, { useState } from 'react';
import { BlogPostCard } from '../components/BlogPostCard';
import { Search, FileX } from 'lucide-react';

const BlogPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const posts = [
    {
      title: "Demystifying Python: Why It's the Perfect First Language",
      excerpt:
        "Many aspiring developers wonder where to start. We break down why Python's readability and versatility make it an ideal choice for beginners in the tech world.",
      imageUrl: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?q=80&w=2070&auto=format&fit=crop',
      author: 'Adoma Mensah',
      authorAvatar: 'https://placehold.co/100x100',
      date: 'October 10, 2023',
      content: `Python consistently ranks as one of the most popular programming languages in the world, and for good reason. Its clean syntax and emphasis on readability make it incredibly approachable for those new to coding. Unlike languages that require strict rules and boilerplate code just to print "Hello, World!", Python lets you get straight to the logic. This gentle learning curve helps build confidence and keeps motivation high.

Beyond its simplicity, Python is a powerhouse of versatility. It's the language of choice for data science and machine learning, with libraries like Pandas, NumPy, and TensorFlow dominating the field. Web developers use frameworks like Django and Flask to build robust, scalable websites and APIs. It's also used for scripting, automation, game development, and more. Learning Python doesn't just teach you to code; it opens doors to a vast array of career paths in the tech industry.`,
    },
    {
      title: 'From Data to Decisions: A Guide to Data Literacy',
      excerpt:
        'In a world driven by data, being data-literate is a superpower. Learn the key concepts that can help you interpret data and make informed decisions in any field.',
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop',
      author: 'Kwame Annan',
      authorAvatar: 'https://placehold.co/100x100',
      date: 'September 22, 2023',
      content: `Data literacy is the ability to read, work with, analyze, and argue with data. It’s a skill that's becoming as fundamental as reading and writing. In our daily lives, we're bombarded with charts, statistics, and figures. Being able to critically assess this information is crucial for making informed decisions, whether you're a business leader, a student, or just a curious citizen.

At its core, data literacy involves understanding what data represents, how it was collected, and what limitations it might have. It's about asking the right questions: Is this sample size representative? Is there a correlation or a causation? What story is this visualization telling, and what is it leaving out? By developing these skills, you can move from passively consuming information to actively engaging with it, turning raw data into meaningful insights.`,
    },
    {
      title: 'The Power of Community in Tech',
      excerpt:
        'Learning to code can be challenging, but you don’t have to do it alone. Discover how joining a tech community can accelerate your growth and open new doors.',
      imageUrl: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?q=80&w=2070&auto=format&fit=crop',
      author: 'Fatima Ibrahim',
      authorAvatar: 'https://placehold.co/100x100',
      date: 'August 15, 2023',
      content: `The journey into technology can often feel solitary. Hours spent debugging code or grappling with complex concepts can be isolating. However, one of the most powerful resources available to any learner is a community. Engaging with a group of like-minded peers provides a support system that is invaluable for overcoming challenges and staying motivated.

Tech communities, like the one we foster at Digital Wave, are more than just forums for asking questions. They are ecosystems of shared knowledge, collaboration, and mentorship. They provide opportunities to work on projects together, get feedback on your work, and network with professionals who can offer guidance and open doors to future opportunities. Whether online or in-person, being part of a community transforms a solo pursuit into a shared journey, making the path to success both faster and more enjoyable.`,
    },
    {
      title: 'Building Your First Website with HTML & CSS',
      excerpt:
        'Take your first steps into web development. This beginner-friendly guide walks you through the essential tags and properties to create a simple, stylish webpage.',
      imageUrl: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?q=80&w=2070&auto=format&fit=crop',
      author: 'Samuel Osei',
      authorAvatar: 'https://placehold.co/100x100',
      date: 'July 30, 2023',
      content: `Every website you've ever visited is built on the foundation of HTML and CSS. HTML (HyperText Markup Language) provides the structure—the headings, paragraphs, and images—while CSS (Cascading Style Sheets) provides the style—the colors, fonts, and layouts. Mastering these two languages is the essential first step for anyone aspiring to become a web developer.

This guide will walk you through the basics. We'll start with a simple HTML document, learning about essential tags like <h1>, <p>, <img>, and <a>. You'll learn how to structure content logically. Then, we'll dive into CSS, exploring how to select HTML elements and apply styles. We'll cover properties for changing colors, adjusting spacing, and creating basic layouts. By the end, you'll have built your very own simple, yet stylish, personal webpage from scratch.`,
    },
    {
      title: 'How Hackathons Can Supercharge Your Career',
      excerpt:
        'More than just coding competitions, hackathons are a launchpad for innovation, networking, and rapid skill development. Here’s why you should join one.',
      imageUrl: 'https://images.unsplash.com/photo-1587620962725-abab7fe55159?q=80&w=2231&auto=format&fit=crop',
      author: 'Aisha Bello',
      authorAvatar: 'https://placehold.co/100x100',
      date: 'June 18, 2023',
      content: `A hackathon is an intense, collaborative event where teams come together to build a project from scratch in a short amount of time, usually 24 to 48 hours. While the competitive aspect is exciting, the true value of a hackathon lies far beyond the prizes. It's an unparalleled opportunity for accelerated learning. You're forced to pick up new technologies, solve problems under pressure, and work effectively in a team.

Furthermore, hackathons are incredible networking events. You'll meet fellow developers, designers, and mentors from various companies. Many participants have found co-founders for startups or landed job interviews through connections made at a hackathon. It’s a chance to showcase your skills, your passion, and your ability to deliver, making it a powerful addition to your portfolio and a significant boost to your career prospects.`,
    },
    {
      title: 'Landing Your First Tech Job: Tips from the Inside',
      excerpt:
        'Navigating the tech job market can be tough. Our lead mentor shares practical tips on building a portfolio, acing interviews, and standing out to recruiters.',
      imageUrl: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2070&auto=format&fit=crop',
      author: 'Daniel K. Addo',
      authorAvatar: 'https://placehold.co/100x100',
      date: 'May 05, 2023',
      content: `Breaking into the tech industry can feel daunting, especially for junior developers. The key is to focus on what you can control: your skills, your portfolio, and your preparation. Your portfolio is your single most important asset. It's not about having dozens of projects; it's about having 2-3 high-quality, well-documented projects that showcase your ability to solve real-world problems. Make sure your code is clean, your READMEs are thorough, and the projects are deployed live.

When it comes to interviews, preparation is everything. Practice common data structures and algorithms, but don't stop there. Be ready to talk in-depth about the projects in your portfolio. Explain your design choices, the challenges you faced, and what you learned. Finally, remember that soft skills matter. Show enthusiasm, be a clear communicator, and demonstrate your passion for learning. Companies aren't just hiring a set of skills; they're hiring a future team member.`,
    },
  ];

  const filteredPosts = posts.filter((post) => {
    const query = searchQuery.toLowerCase();
    return (
      post.title.toLowerCase().includes(query) ||
      post.excerpt.toLowerCase().includes(query) ||
      post.author.toLowerCase().includes(query)
    );
  });

  return (
    <div className='animate-fade-in'>
      <section className='py-16 text-center bg-muted'>
        <div className='container mx-auto px-6'>
          <h1 className='text-4xl font-bold text-foreground font-heading opacity-0 animate-fade-in-up'>
            DigitalWave <span className='text-primary'>Blog</span>
          </h1>
          <p
            className='text-lg text-muted-foreground mt-2 max-w-2xl mx-auto opacity-0 animate-fade-in-up'
            style={{ animationDelay: '0.2s' }}
          >
            Insights, tutorials, and stories from our community.
          </p>
        </div>
      </section>

      <section className='py-20'>
        <div className='container mx-auto px-6'>
          {/* Search Bar */}
          <div className='max-w-md mx-auto mb-12 opacity-0 animate-fade-in' style={{ animationDelay: '0.3s' }}>
            <div className='relative'>
              <span className='absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground'>
                <Search className='h-5 w-5' />
              </span>
              <input
                type='text'
                placeholder='Search articles by title, keyword, or author...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='w-full bg-background border border-input rounded-md py-3 pl-10 pr-4 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-shadow shadow-sm'
                aria-label='Search blog posts'
              />
            </div>
          </div>

          {filteredPosts.length > 0 ? (
            <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
              {filteredPosts.map((post, index) => (
                <div
                  key={post.title}
                  className='opacity-0 animate-fade-in-up'
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <BlogPostCard post={post} />
                </div>
              ))}
            </div>
          ) : (
            <div className='text-center py-16 opacity-0 animate-fade-in bg-card rounded-lg border border-border border-dashed'>
              <div className='text-muted-foreground mb-2'>
                <FileX className='h-12 w-12 mx-auto mb-3 text-muted-foreground/50' />
              </div>
              <h3 className='text-lg font-medium text-foreground'>No articles found</h3>
              <p className='text-muted-foreground'>We couldn't find any posts matching "{searchQuery}".</p>
              <button onClick={() => setSearchQuery('')} className='mt-4 text-primary hover:underline font-medium'>
                View all articles
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
